/**
 * The text colour of one banner: a value of the `textColorLocked` record.
 *
 * - "black" and "light" are the two locks of Alt+Click (hooks/useLogoCycling).
 * - "#rrggbb" is a colour that an admin chose in the Defaults panel.
 * - Null or no value: the text follows the logo and the background.
 *
 * A lock colours the text of the banner and the header badge. The banner
 * buttons keep their own colours. With "black" and "light" the product name
 * stays in the colour of the logo; a chosen colour also colours the product
 * name, but not in the header, where it is part of the logo
 * (App.css, `.text-color-locked`).
 */
export type HexTextColor = `#${string}`;
export type TextColorLock = "black" | "light" | HexTextColor;

const HEX_TEXT_COLOR = /^#[0-9a-f]{6}$/i;

export function isHexTextColor(value: unknown): value is HexTextColor {
  return typeof value === "string" && HEX_TEXT_COLOR.test(value);
}

export function isTextColorLock(value: unknown): value is TextColorLock {
  return value === "black" || value === "light" || isHexTextColor(value);
}

/** True when text in this colour is dark (the brightness formula of W3C AERT). */
function isDarkHex(hex: HexTextColor): boolean {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 150;
}

export interface TextColorStyle {
  /** The CSS colour of the text. */
  color: string;
  /** Dark text: the banner takes the styles of black text (logo dashes, badge pills). */
  dark: boolean;
  /** The product name outside the header takes the colour too. */
  coversProductName: boolean;
}

/** The style of a lock, or null when the text is not locked. */
export function textColorStyle(value: unknown): TextColorStyle | null {
  if (value === "black") return {color: "#000", dark: true, coversProductName: false};
  if (value === "light") return {color: "#fff", dark: false, coversProductName: false};
  if (isHexTextColor(value)) {
    return {color: value.toLowerCase(), dark: isDarkHex(value), coversProductName: true};
  }
  return null;
}

/**
 * The colour that all the given banners share, or null when they differ or
 * one of them has no chosen colour.
 */
export function sharedHexTextColor(
  record: Record<string, unknown>,
  ids: readonly string[]
): HexTextColor | null {
  if (ids.length === 0) return null;
  const first = record[ids[0]];
  if (!isHexTextColor(first)) return null;
  const color = first.toLowerCase() as HexTextColor;
  return ids.every((id) => {
    const value = record[id];
    return isHexTextColor(value) && value.toLowerCase() === color;
  })
    ? color
    : null;
}
